import { Injectable, inject } from '@angular/core';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { StructureService } from '@app/shared/structure/structure.service';
import { Espace, ObjetMaquetteDetail, ObjetsMaquetteService } from '@pcscol/odf-api-v1-angular';
import { Orientation } from '@swimlane/ngx-graph';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { TreeNode } from '../components/treeview/tree-node';
import { EspaceService } from './espace.service';

export interface TreeViewFiltres {
    partieHaute: boolean;
    partieBasse: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class TreeViewService {
    private _objetMaquetteService = inject(ObjetsMaquetteService);
    private _structureService = inject(StructureService);
    private _espaceService = inject(EspaceService);
    private _autorisationsService = inject(AutorisationsService);

    constructor() {
        this.listenToRacineIdChangesAndLoadRacineMaquetteAndEspaceAccordingly();
        this.listenToEspaceChangesAndResetRacineAccordingly();
    }

    private listenToRacineIdChangesAndLoadRacineMaquetteAndEspaceAccordingly() {
        this._racineMaquetteId
            .pipe(
                switchMap((id: string | null) =>
                    id ? this._objetMaquetteService.lireObjetMaquette(this._structureService.current$.getValue(), id) : of(null)
                ),
                tap(v => {
                    if (v?.espace) {
                        this._espaceService.selectEspaceById(v.espace);
                    }
                })
            )
            .subscribe((value: ObjetMaquetteDetail | null) => {
                this.racineMaquette$.next(value);
            });
    }

    private listenToEspaceChangesAndResetRacineAccordingly() {
        this._espaceService.espaceSelected$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)).subscribe((espace: Espace | null) => {
            if (!!espace && this.racineMaquette$.getValue() && espace?.id !== this.racineMaquette$.getValue()?.espace) {
                this.racineMaquetteId = null;
            }
        });
    }

    readonly objetMaquetteSelected$ = new BehaviorSubject<TreeNode | null>(null);
    public selectObjetMaquette(om: TreeNode | null): void {
        this.parentSelectedForAddingChild$.next(null);
        this.objetMaquetteSelected$.next(om);
        this.contexteIdSelected$.next(om?.contexteId ?? null);
    }

    readonly contexteIdSelected$ = new BehaviorSubject<string | null>(null);

    readonly parentSelectedForAddingChild$ = new BehaviorSubject<{ parent: TreeNode; enfantDejaExistant: boolean } | null>(null);
    public clearParentSelectedForAddingChild(): void {
        this.parentSelectedForAddingChild$.next(null);
    }
    public selectParentForAddingChild(event: { parent: TreeNode; enfantDejaExistant: boolean }): void {
        this.parentSelectedForAddingChild$.next(event);
    }

    /**
     * Selection de la maquette (racine)
     */
    private _racineMaquetteId = new BehaviorSubject<string | null>(null);
    readonly racineMaquette$ = new BehaviorSubject<ObjetMaquetteDetail | null>(null);
    readonly maquetteHistory$ = this._autorisationsService.isAutorise$(Traitement.ODF_LIRE_HISTORIQUE).pipe(
        filter(autorise => !!autorise),
        switchMap(() =>
            this._racineMaquetteId.asObservable().pipe(
                tap(id => {
                    console.log(
                        '_racineMaquetteId sélectionné dans le service %c' + id,
                        'color: green; font-weight: bold; font-size: 1.1em;'
                    );
                }),
                switchMap((id: string | null) =>
                    id ? this._objetMaquetteService.lireHistorique(this._structureService.current$.getValue(), id) : of(null)
                )
            )
        )
    );

    public set racineMaquetteId(id: string | null) {
        if (!this._racineMaquetteId.observers.length) {
            this.listenToRacineIdChangesAndLoadRacineMaquetteAndEspaceAccordingly();
        }
        this._racineMaquetteId.next(id);
    }

    /**
     * Rafraichit l'arbre dans la treeview
     */
    private _refresh = new Subject<void>();
    public refresh$: Observable<void> = this._refresh.asObservable();
    public refresh() {
        this._refresh.next();
    }

    /**
     * pliage/dépliage de l'arbre
     */
    private _expandNodeById = new Subject<string>();
    private _expandTree = new Subject<boolean>();
    public expandNodeById$ = this._expandNodeById.asObservable();
    public expandTree$ = this._expandTree.asObservable();
    public expandTree(expand: boolean) {
        this._expandTree.next(expand);
    }
    public expandNode(id: string) {
        this._expandNodeById.next(id);
    }

    /**
     * Filtres de l'arbre
     */

    private _filtrePartiesArbre = new BehaviorSubject<TreeViewFiltres>({ partieHaute: true, partieBasse: true });
    private _rechercheTexteDansArbre = new BehaviorSubject<string | null>(null);
    _rechercheActuelleCount = new BehaviorSubject<{ index: number | null; totalElements: number | null } | null>(null);

    public filtresPartiesArbre$ = this._filtrePartiesArbre.asObservable();
    public rechercheTexteDansArbre$ = this._rechercheTexteDansArbre
        .asObservable()
        .pipe(map((s: string | null) => (typeof s === 'string' ? s.toLowerCase() : s)));
    public rechercheActuelleCount$ = this._rechercheActuelleCount.asObservable();

    public get filtresPartiesArbreActuels(): TreeViewFiltres {
        return this._filtrePartiesArbre.getValue();
    }

    public get rechercheTexteActuelle(): string | null {
        return this._rechercheTexteDansArbre.getValue();
    }
    public togglePartieHaute(): void {
        this._filtrePartiesArbre.next({
            partieBasse: true,
            partieHaute: !this._filtrePartiesArbre.getValue().partieHaute
        });
    }
    public togglePartieBasse(): void {
        this._filtrePartiesArbre.next({
            partieHaute: true,
            partieBasse: !this._filtrePartiesArbre.getValue().partieBasse
        });
    }

    public lancerRechercheTexte(texte: string | null): void {
        if (this.rechercheTexteActuelle === texte) {
            const index =
                this._rechercheActuelleCount.getValue()?.index !== undefined ? this._rechercheActuelleCount.getValue()!.index! + 1 : 0;
            const totalElements = this._rechercheActuelleCount.getValue()?.totalElements || 0;
            this._rechercheActuelleCount.next({ index: totalElements !== 0 ? index % totalElements : 0, totalElements });
        } else {
            this._rechercheTexteDansArbre.next(texte);
        }
    }

    /**
     * TabsLayout: gestion des tabs: vertical/horizontal
     */
    private _tabsLayout = new BehaviorSubject<'vertical' | 'horizontal'>('vertical');
    public tabsLayout$ = this._tabsLayout.asObservable();
    public toggleTabsLayout() {
        if (this._tabsLayout.getValue() === 'vertical') {
            this._tabsLayout.next('horizontal');
        } else {
            this._tabsLayout.next('vertical');
        }
    }

    /**
     * SideNav: gestion des tabs: vertical/horizontal
     */
    private _sidebarLayoutOpened = new BehaviorSubject<boolean>(true);
    public sidebarOpened$ = this._sidebarLayoutOpened.asObservable();
    public toggleSidebar() {
        this._sidebarLayoutOpened.next(!this._sidebarLayoutOpened.getValue());
    }
    public openSidebar(): void {
        this._sidebarLayoutOpened.next(true);
    }
    public closeSidebar(): void {
        this._sidebarLayoutOpened.next(false);
    }

    /**
     * Vue graphique
     */

    private _viewLayout = new BehaviorSubject<'graph' | 'tree'>('tree');
    public viewLayout$ = this._viewLayout.asObservable();
    public toggleViewLayout() {
        if (this._viewLayout.getValue() === 'tree') {
            this.closeSidebar();
            this._viewLayout.next('graph');
        } else {
            this.openSidebar();
            this._viewLayout.next('tree');
        }
    }

    private _graphOrientation = new BehaviorSubject<Orientation>(Orientation.TOP_TO_BOTTOM);
    public graphOrientation$ = this._graphOrientation.asObservable();
    toggleGraphOrientation() {
        if (this._graphOrientation.getValue() === Orientation.TOP_TO_BOTTOM) {
            this._graphOrientation.next(Orientation.LEFT_TO_RIGHT);
        } else {
            this._graphOrientation.next(Orientation.TOP_TO_BOTTOM);
        }
    }
}
